import type { SxProps, Theme } from '@mui/material/styles';
import Snackbar from '@mui/material/Snackbar';
import PropTypes from 'prop-types';
import { useMeasuredBottomSnackbarStackIndex } from './SnackbarStackContext';
import {
  bottomCenterSnackbarStackSx,
  bottomLeftSnackbarStackSx,
  centeredPlainSnackbarContentSx,
  defaultSnackbarContentSx,
  mergeSx,
} from './snackbarStyles';

interface BasicSnackBarProps {
  onClose: (open: boolean) => void;
  onExited?: () => void;
  open: boolean;
  message: string;
  vertical: 'top' | 'bottom';
  horizontal: 'center' | 'left' | 'right';
  persistent?: boolean;
  autoHideDuration?: number | null;
  disableStackRegistration?: boolean;
  stackSlots?: number;
  contentSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
}

const BasicSnackBar = (props: BasicSnackBarProps) => {
  const {
    onClose,
    onExited,
    open,
    message,
    vertical,
    horizontal,
    persistent = false,
    autoHideDuration = 3500,
    disableStackRegistration = false,
    stackSlots = 1,
    contentSx,
    sx,
  } = props;
  const usesBottomStack = vertical === 'bottom' && (horizontal === 'center' || horizontal === 'left');
  const { ref: snackbarRef, stackIndex } = useMeasuredBottomSnackbarStackIndex<HTMLDivElement>({
    disabled: !usesBottomStack || disableStackRegistration,
    horizontal: horizontal === 'left' ? 'left' : 'center',
    open,
    slots: stackSlots,
  });
  const stackSx =
    usesBottomStack && !disableStackRegistration
      ? horizontal === 'left'
        ? bottomLeftSnackbarStackSx(stackIndex)
        : bottomCenterSnackbarStackSx(stackIndex)
      : undefined;

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (persistent && (reason === 'clickaway' || reason === 'escapeKeyDown')) {
      return;
    }
    onClose(false);
  };

  return (
    <Snackbar
      anchorOrigin={{ vertical, horizontal }}
      autoHideDuration={persistent ? null : autoHideDuration}
      ref={snackbarRef}
      open={open}
      onClose={handleClose}
      message={message}
      key={`${vertical}-${horizontal}-${persistent ? 'persistent' : 'temporary'}-${message}`}
      sx={mergeSx(stackSx, sx)}
      slotProps={{
        content: {
          sx: mergeSx(centeredPlainSnackbarContentSx, contentSx ?? defaultSnackbarContentSx),
        },
        transition: {
          onExited,
        },
      }}
    />
  );
};

BasicSnackBar.propTypes = {
  onClose: PropTypes.func.isRequired,
  onExited: PropTypes.func,
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  vertical: PropTypes.string.isRequired,
  horizontal: PropTypes.string.isRequired,
  persistent: PropTypes.bool,
  autoHideDuration: PropTypes.number,
  disableStackRegistration: PropTypes.bool,
  stackSlots: PropTypes.number,
  contentSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export default BasicSnackBar;
